import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../base/pd-base-ui-input.js";
import { PdBaseInputElement } from "../base/pd-base-input-element.js";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-select/PdSelect.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*
* Web component to render a select input using LitElement.
*
* Used as `pd-select`.
*/
/**
* Select dropdown component for forms.
*
* @tagname pd-select
* @summary Styled select dropdown with native accessibility.
*
* @event validate-form - Fired when selection changes for validation.
* @event field-change - Fired when selection changes.
*
* @cssprop --pd-select-arrow-col - Arrow colour. Default: `var(--pd-default-col)`. Error state: `--pd-select-arrow-col-error`, disabled: `var(--pd-default-disabled-col)`.
* @cssprop --pd-select-arrow-col-error - Arrow colour in the error state. Default: `var(--pd-default-error-col)`.
* @cssprop --pd-select-arrow-size - Arrow width/height. Default: `0.65em`.
* @cssprop --pd-select-arrow-image - Arrow SHAPE as `url(...)` (used as CSS mask — only the alpha channel counts, colour comes from `--pd-select-arrow-col`). Default: built-in chevron.
* @cssprop --pd-input-field-width - Select width. Default: `250px`.
* @cssprop --pd-input-field-height - Select height. Default: `2.2rem`.
* @cssprop --pd-input-field-padding - Select padding. Default: `0.25rem`.
* @cssprop --pd-input-field-bg-col - Background color. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-input-field-border - Border style. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-input-field-border-bottom - Bottom border. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-input-field-border-focus - Focus border. Default: `2px solid var(--pd-default-col)`.
* @cssprop --pd-input-field-border-col-hover - Hover border color. Default: `var(--pd-default-hover-col)`.
*/
var PdSelect = class extends PdBaseInputElement {
	static {
		this.styles = [PdBaseInputElement.styles, css`
      /* Der Pfeil liegt als ::after auf dem .input-Wrapper (ein <select> kann
         keine Pseudo-Elemente tragen). Form = Maske aus --pd-select-arrow-image,
         Farbe = background-color aus --pd-select-arrow-col → themebar, anders als
         ein Data-URI mit festem fill. */
      .input {
        position: relative;
      }

      .input::after {
        content: "";
        position: absolute;
        top: 50%;
        right: 0.5em;
        width: var(--pd-select-arrow-size, 0.65em);
        height: var(--pd-select-arrow-size, 0.65em);
        transform: translateY(-50%);
        pointer-events: none;
        background-color: var(--pd-select-arrow-col, var(--pd-default-col));
        -webkit-mask: var(
            --pd-select-arrow-image,
            url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23000%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")
          )
          center / contain no-repeat;
        mask: var(
            --pd-select-arrow-image,
            url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23000%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")
          )
          center / contain no-repeat;
      }

      .input.error::after {
        background-color: var(
          --pd-select-arrow-col-error,
          var(--pd-default-error-col)
        );
      }

      .input:has(select:disabled)::after {
        background-color: var(--pd-default-disabled-col);
      }

      .select-css {
        display: block;
        -moz-appearance: none;
        -webkit-appearance: none;
        appearance: none;
        padding: var(--pd-input-field-padding, 0.25rem);
        /* Platz für den Pfeil, damit lange Optionstexte nicht darunterlaufen */
        padding-right: calc(var(--pd-input-field-padding, 0.25rem) + 1.5em);
        max-width: 100%;
      }

      .select-css::-ms-expand {
        display: none;
      }

      .select-css option {
        font-weight: normal;
        text-indent: 5px;
      }
    `];
	}
	constructor() {
		super();
		this.values = [];
		this._inputType = 2;
	}
	update(changedProps) {
		if (changedProps.has("values") && this.values?.length > 0 && !this.initValue && !this._value) this._value = this.values[0].value;
		super.update(changedProps);
	}
	render() {
		const selectId = `${this.id}Select`;
		const errorId = `${this.id}Error`;
		return html`
      ${this._renderLabel(selectId)}
      <div
        class="${classMap(this.getClassmap({ input: true }))}"
      >
        <select
          id="${selectId}"
          class="input-style select-css ${this.gradient ? "gradient" : ""}"
          ?disabled="${this.disabled}"
          name="${this.name || this.valueName || this.autoCompleteName}"
          autocomplete="${this.autoCompleteName ?? "off"}"
          aria-invalid="${this._invalid}"
          aria-describedby="${this._errorMsg ? errorId : ""}"
          @change="${this._onSelectChange}"
          @blur="${this._onBlur}"
          @focus="${this._onFocus}"
        >
          ${this._renderOptions()}
        </select>
      </div>
      ${this._renderErrorMsg(errorId)}
    `;
	}
	_renderOption(val) {
		return html`
      <option
        value="${val.value}"
        ?selected="${String(this._value) === String(val.value)}"
      >
        ${val.name}
      </option>
    `;
	}
	_renderOptions() {
		const result = [];
		let currentGroup;
		let groupOptions = [];
		for (const val of this.values) {
			if (val.group !== currentGroup) {
				if (currentGroup != null) {
					result.push(html`<optgroup label="${currentGroup}">${groupOptions}</optgroup>`);
					groupOptions = [];
				}
				currentGroup = val.group;
			}
			if (val.group) groupOptions.push(this._renderOption(val));
			else {
				currentGroup = void 0;
				result.push(this._renderOption(val));
			}
		}
		if (currentGroup != null && groupOptions.length > 0) result.push(html`<optgroup label="${currentGroup}">${groupOptions}</optgroup>`);
		return result;
	}
	_onSelectChange(event) {
		this._handleChangedValue(this._input.value, event, true);
	}
	_getInitialValue() {
		return this.initValue || this.values[0]?.value || "";
	}
	clear() {
		this.reset();
	}
};
__decorate([property({ type: Array })], PdSelect.prototype, "values", void 0);
//#endregion
export { PdSelect };
